/** Sales navigation shares the existing invoice register. Workspace owners
 * land on invoices; project valuations remain in their project workspace.
 * Platform administrators retain their own billing overview and subscriptions.
 */

import {
  LayoutDashboard,
  Receipt,
  Link as LinkIcon,
  CreditCard,
  FileText,
  Package,
  Users,
  Settings,
  Repeat,
} from '@/shared/ui/icons';
import { isAdministratorRole } from '../../utils/permissions';

export type SalesSectionId =
  | 'overview'
  | 'invoices'
  | 'subscriptions'
  | 'payment-links'
  | 'online-payments'
  | 'quotes'
  | 'products'
  | 'customers'
  | 'settings';

export interface SalesSection {
  id: SalesSectionId;
  label: string;
  Icon: React.ElementType;
  /** No workspace equivalent, so it is filtered out rather than re-sourced. */
  adminOnly?: boolean;
}

export const SALES_SECTIONS: SalesSection[] = [
  { id: 'overview',         label: 'Sales Overview',        Icon: LayoutDashboard, adminOnly: true },
  { id: 'invoices',         label: 'Invoices',              Icon: Receipt },
  { id: 'subscriptions',    label: 'Subscriptions',         Icon: Repeat, adminOnly: true },
  { id: 'payment-links',    label: 'Payment Links',         Icon: LinkIcon },
  { id: 'online-payments',  label: 'Online Payments',       Icon: CreditCard },
  { id: 'quotes',           label: 'Quotes',                Icon: FileText },
  { id: 'products',         label: 'Products and Services', Icon: Package },
  { id: 'customers',        label: 'Customers',             Icon: Users },
  { id: 'settings',         label: 'Sales Settings',        Icon: Settings },
];

export function visibleSalesSections(role?: string): SalesSection[] {
  const isAdmin = isAdministratorRole(role as never);
  return SALES_SECTIONS.filter((s) => !s.adminOnly || isAdmin);
}

/** Sections that are still placeholders. Kept here so the hub and any nav that
 *  wants to mark them agree on which are real. */
export const SALES_COMING_SOON: Partial<Record<SalesSectionId, string>> = {
  'payment-links':   'Payment Links will be available soon.',
  'online-payments': 'Online Payments will be available soon.',
  'quotes':          'Quotes will be available soon.',
  'products':        'Products and Services will be available soon.',
  'customers':       'Customers will be available soon.',
  'settings':        'Sales Settings will be available soon.',
};
